import assignOffsetPagination from "@/schemas/assignOffsetPagination";
import { WishlistItemEntity } from "@apps/wishlist/entities/WishlistItemEntity";
import { WishlistsBackInStockItemEntity } from "@apps/wishlist/entities/WishlistsBackInStockItemEntity";
import { WishlistsItemsTotalCountEntity } from "@apps/wishlist/entities/WishlistsItemsTotalCountEntity";
import { WishlistWithItemsEntity } from "@apps/wishlist/entities/WishlistWithItemsEntity";
import { WishlistItemSchema } from "@apps/wishlist/schemas/WishlistItemSchema";
import { WishlistsItemsTotalCountSchema } from "@apps/wishlist/schemas/WishlistsItemsTotalCountSchema";
import { getBackInStockWishlistItemsParams } from "@apps/wishlist/services/WishlistsBackInStockItemsService";
import { wishlistAuthApiClientToken } from "@apps/wishlist/tokens";
import {
    QueryPaginationMeta,
    WISHLIST_ITEMS_SORTING_TYPE,
} from "@apps/wishlist/types";
import { JsonMimeType } from "@lib/ApiClient";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { I18nModule } from "@modules/i18n/I18nModule";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { create } from "superstruct";
import { inject, injectable } from "tsyringe";

import { CopyWishlistResponseSchema } from "./responses/CopyWishlistResponseSchema";
import { CreateWishlistResponseSchema } from "./responses/CreateWishlistResponseSchema";
import { DeleteItemResponseSchema } from "./responses/DeleteItemResponseSchema";
import { DeleteWishlistResponseSchema } from "./responses/DeleteWishlistResponseSchema";
import { GetBackInStockItemsResponseSchema } from "./responses/GetBackInStockItemsResponseSchema";
import { GetWishlistsResponseSchema } from "./responses/GetWishlistsResponseSchema";
import { MoveItemResponseSchema } from "./responses/MoveItemResponseSchema";
import { ProductStatusesResponseSchema } from "./responses/ProductStatusesResponseSchema";
import { UpdateItemVariantResponseSchema } from "./responses/UpdateItemVariantResponseSchema";
import { UpdateWishlistResponseSchema } from "./responses/UpdateWishlistResponseSchema";

export type GetWishlistsParams = {
    orderBy?: WISHLIST_ITEMS_SORTING_TYPE;
};

export type UpdateWishlistRequestBody = {
    wishlistId: number;
    newTitle?: string;
    isDefault?: boolean;
    isPrivate?: boolean;
};

@injectable()
export class WishlistsApiService {
    private readonly locale: string;

    public constructor(
        @inject(wishlistAuthApiClientToken)
        private readonly apiClient: AuthApiClient,
        @inject(I18nModule)
        private readonly i18nModule: I18nModule
    ) {
        this.locale =
            this.i18nModule.exports.localizationInfoProvider.getGwI18nLocale();
    }

    getWishlists(
        queryParams?: GetWishlistsParams
    ): Observable<WishlistWithItemsEntity[]> {
        return this.apiClient
            .get({
                url: "/getWishlists",
                responseType: "json",
                queryParams: { ...queryParams, locale: this.locale },
            })
            .pipe(
                map((response) => {
                    return create(response.body, GetWishlistsResponseSchema);
                }),
                map((result) => {
                    return result.wishlistsWithItems;
                })
            );
    }

    getWishlistItems(queryParams: QueryPaginationMeta) {
        return this.apiClient
            .get({
                url: "/getItems",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        assignOffsetPagination(WishlistItemSchema)
                    );
                })
            );
    }

    getItemsTotalCount(): Observable<WishlistsItemsTotalCountEntity> {
        return this.apiClient
            .get({
                url: "/getItemsTotalCount",
                responseType: "json",
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        WishlistsItemsTotalCountSchema
                    );
                })
            );
    }

    mergeWishlists(saveWishlistsToken: string): Observable<unknown> {
        return this.apiClient
            .post({
                url: "/attachSavedWishlists",
                body: JSON.stringify({ saveWishlistsToken }),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((result) => {
                    return result;
                })
            );
    }

    saveWishlists(
        email: string,
        isAcceptsEmailMarketing: boolean
    ): Observable<unknown> {
        return this.apiClient
            .post({
                url: "/saveWishlistItems",
                body: JSON.stringify({
                    email,
                    isAcceptsEmailMarketing,
                }),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((result) => {
                    return result;
                })
            );
    }

    addItem(data: {
        wishlistId: number;
        productId: number;
        variantId: number;
    }): Observable<WishlistItemEntity> {
        return this.apiClient
            .post({
                url: "/addItem",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, WishlistItemSchema);
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public deleteItem(data: {
        wishlistId: number | null;
        productId: number;
        variantId: number | null;
    }): Observable<WishlistItemEntity> {
        return this.apiClient
            .delete({
                url: "/deleteItem",
                queryParams: {
                    ...(data.wishlistId
                        ? { wishlistId: data.wishlistId }
                        : undefined),
                    ...(data.variantId
                        ? { variantId: data.variantId }
                        : undefined),
                    productId: data.productId,
                },
            })
            .pipe(
                map((response) => {
                    return create(response.body, DeleteItemResponseSchema);
                }),
                map((result) => {
                    return result.deletedItem;
                })
            );
    }

    public moveItem(data: {
        wishlistId: number;
        productId: number;
        variantId: number | null;
        targetWishlistId: number;
    }): Observable<WishlistItemEntity> {
        return this.apiClient
            .post({
                url: "/moveItem",
                body: JSON.stringify(
                    data.variantId
                        ? {
                              wishlistId: data.wishlistId,
                              productId: data.productId,
                              variantId: data.variantId,
                              targetWishlistId: data.targetWishlistId,
                          }
                        : {
                              wishlistId: data.wishlistId,
                              productId: data.productId,
                              targetWishlistId: data.targetWishlistId,
                          }
                ),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, MoveItemResponseSchema);
                }),
                map((result) => {
                    return result.movedItem;
                })
            );
    }

    public updateWishlist(data: UpdateWishlistRequestBody) {
        return this.apiClient
            .post({
                url: "/updateWishlist",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, UpdateWishlistResponseSchema);
                }),
                map((result) => {
                    return result.updatedWishlist;
                })
            );
    }

    public createWishlist(data: { title: string }) {
        return this.apiClient
            .post({
                url: "/createWishlist",
                body: JSON.stringify({
                    title: data.title,
                }),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, CreateWishlistResponseSchema);
                }),
                map((result) => {
                    return result.createdWishlist;
                })
            );
    }

    public deleteWishlist(data: { wishlistId: number }) {
        return this.apiClient
            .delete({
                url: "/deleteWishlist",
                queryParams: {
                    wishlistId: data.wishlistId,
                },
            })
            .pipe(
                map((response) => {
                    return create(response.body, DeleteWishlistResponseSchema);
                }),
                map((result) => {
                    return result.deletedWishlist;
                })
            );
    }

    public copyWishlist(data: { wishlistId: number }) {
        return this.apiClient
            .post({
                url: "/copyWishlist",
                body: JSON.stringify({ wishlistId: data.wishlistId }),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, CopyWishlistResponseSchema);
                }),
                map((result) => {
                    return result.wishlistsWithItems;
                })
            );
    }

    public getWishlistProductStatuses(data: { productIds: number[] }) {
        return this.apiClient
            .post({
                url: "/getWishlistProductStatuses",
                body: JSON.stringify({
                    productIds: data.productIds,
                }),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, ProductStatusesResponseSchema);
                }),
                map((result) => {
                    return result.results;
                })
            );
    }

    public getBackInStockWishlistItems(
        params: getBackInStockWishlistItemsParams
    ): Observable<WishlistsBackInStockItemEntity[]> {
        return this.apiClient
            .get({
                url: "/getBackInStockWishlistItems",
                queryParams: {
                    wishlistItems: JSON.stringify(params),
                },
                responseType: "json",
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        GetBackInStockItemsResponseSchema
                    );
                }),
                map((response) => {
                    return response.results;
                })
            );
    }

    public updateWishlistItemVariant(data: {
        wishlistId: number;
        productId: number;
        oldVariantId: number;
        newVariantId: number;
    }) {
        return this.apiClient
            .put({
                url: "/updateWishlistItemVariant",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        UpdateItemVariantResponseSchema
                    );
                }),
                map((result) => {
                    return result;
                })
            );
    }
}
